// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { successResponseSchema } from '@/api/schemas';
import { DateTime } from 'luxon';

interface AssetAssignment {
  start: DateTime;
  end: DateTime | null;
}

/**
 * Assigns an asset to a project at a specific date and time.
 *
 * @param assetId - The ID of the asset to assign.
 * @param projectKey - The key of the project to which the asset is being
 * assigned.
 * @param assignment - The assignment details.
 * @param assignment.start - The date and time at which the asset assignment
 * starts.
 * @param assignment.end - The date and time at which the asset assignment
 * ends. If this value is null, the assignment is considered to be ongoing.
 */
export async function assignAsset(
  assetId: string,
  projectKey: string,
  assignment: AssetAssignment
) {
  const path = `/v1/project/${projectKey}/asset`;
  const response = await api.post(path, [
    {
      assetId,
      effectiveDate: assignment.start.toISO(),
      endDate: assignment.end?.toISO() ?? null,
    },
  ]);

  return parseResponse(successResponseSchema, response.data, {
    schemaName: 'AssignAsset',
    endpoint: path,
  });
}
